import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ROLES } from '@lms/shared-types';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { SearchInput } from '@/components/SearchInput';
import { api, listAllResource, listResource } from '@/lib/api';
import { enrollmentSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { matchesSearch } from '@/lib/search-normalize';
import { normalizeUserRoles } from '@/lib/user-roles';

interface BulkForm {
  courseId: string;
  userIds: string[];
  reason?: string;
}

interface CourseOption {
  id: string;
  name: string;
  title?: string;
}

interface UserOption {
  id: string;
  name: string;
  fullName?: string;
  email?: string;
  status?: string;
  roles?: unknown;
}

const LABEL = 'text-sm font-medium text-dash-on-surface-variant';
const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

function selectedLabel(count: number): string {
  return `${count} ${count === 1 ? 'seleccionado' : 'seleccionados'}`;
}

export function EnrollmentBulkPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const { register, handleSubmit, watch, setValue } = useForm<BulkForm>({ defaultValues: { userIds: [], courseId: '' } });
  const selectedIds = watch('userIds') ?? [];

  const { data: coursesData } = useQuery({
    queryKey: ['courses'],
    queryFn: () => listResource<CourseOption>('/courses'),
  });

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', 'bulk-enroll'],
    queryFn: async () => {
      const res = await listAllResource<UserOption>('/users');
      return { ...res, data: (res.data ?? []).map((user) => normalizeUserRoles(user)) };
    },
  });

  const mutation = useMutation({
    mutationFn: (values: BulkForm) => api('/enrollments/bulk', { method: 'POST', body: values }),
    onSuccess: async (created) => {
      const count = Array.isArray(created) ? created.length : 1;
      toast(enrollmentSuccessToast('create', count));
      await queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      navigate('/inscripciones');
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo crear la matrícula'));
    },
  });

  const courses = coursesData?.data ?? [];
  const users = usersData?.data ?? [];

  const filteredUsers = useMemo(() => {
    const students = users.filter(
      (user) => user.status === 'active' && (user.roles ?? []).includes(ROLES.STUDENT),
    );
    return students.filter((user) =>
      matchesSearch(`${user.fullName ?? user.name} ${user.email ?? ''}`, search),
    );
  }, [users, search]);

  function toggleUser(userId: string) {
    const next = selectedIds.includes(userId) ? selectedIds.filter((id) => id !== userId) : [...selectedIds, userId];
    setValue('userIds', next, { shouldValidate: true });
  }

  const selectedCount = selectedIds.length;

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <div className="mt-1 shrink-0">
          <HubBackLink to="/inscripciones" />
        </div>
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Matricular estudiantes</h1>
          <p className="mt-1 text-dash-on-surface-variant">Asigna alumnos a cursos específicos de la plataforma.</p>
        </div>
      </div>

      <div className="w-full">
        <div className="lms-credentials-glass-panel rounded-xl border border-dash-outline-variant p-6 shadow-sm md:p-8">
          <form method="post" className="space-y-6" onSubmit={handleSubmit((v) => mutation.mutate(v))}>
            <div className="space-y-2">
              <label htmlFor="assignable_item_id" className={LABEL}>
                Curso <span className="text-error-600">*</span>
              </label>
              <div className="relative">
                <select
                  id="assignable_item_id"
                  required
                  className={`${FIELD} h-12 cursor-pointer appearance-none pr-10`}
                  {...register('courseId', { required: true })}
                >
                  <option value="" disabled>
                    Seleccione un curso...
                  </option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title ?? course.name}
                    </option>
                  ))}
                </select>
                <span
                  className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant"
                  aria-hidden="true"
                >
                  <span className="material-symbols-outlined">expand_more</span>
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-end justify-between gap-4">
                <label className={LABEL}>
                  Alumnos <span className="text-error-600">*</span>
                </label>
                <span
                  className={`text-xs font-semibold ${selectedCount > 0 ? 'text-dash-secondary' : 'text-dash-on-surface-variant'}`}
                >
                  {selectedLabel(selectedCount)}
                </span>
              </div>

              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Buscar alumnos por nombre o correo..."
                aria-label="Buscar alumnos por nombre o correo"
              />

              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-dash-outline-variant bg-white">
                  <div className="max-h-64 overflow-y-auto p-1">
                    <div className="space-y-1">
                      {filteredUsers.length === 0 ? (
                        <p className="px-3 py-6 text-center text-sm text-dash-on-surface-variant">
                          No hay alumnos que coincidan con la búsqueda.
                        </p>
                      ) : (
                        filteredUsers.map((user) => {
                          const checked = selectedIds.includes(user.id);
                          const displayName = user.fullName ?? user.name;

                          return (
                            <label
                              key={user.id}
                              className={`flex cursor-pointer items-center gap-4 rounded-md p-3 transition-colors hover:bg-secondary-50 ${
                                checked ? 'bg-brand-50' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="h-5 w-5 rounded border-dash-outline-variant text-dash-primary focus:ring-dash-secondary/20"
                                checked={checked}
                                onChange={() => toggleUser(user.id)}
                              />
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-dash-on-surface-variant">
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  person
                                </span>
                              </div>
                              <div className="min-w-0 flex-grow">
                                <p className="text-sm font-medium text-dash-on-surface">{displayName}</p>
                                <p className="truncate text-xs text-dash-on-surface-variant">{user.email}</p>
                              </div>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="reason" className={LABEL}>
                Motivo (opcional)
              </label>
              <textarea
                id="reason"
                rows={3}
                placeholder="Indique la razón de la matriculación masiva o notas adicionales..."
                className={`${FIELD} resize-none`}
                {...register('reason')}
              />
            </div>

            <div className="flex flex-col gap-4 pt-4 sm:flex-row">
              <button
                type="submit"
                disabled={mutation.isPending || selectedCount === 0}
                className="inline-flex h-12 flex-1 items-center justify-center rounded-lg bg-dash-primary text-sm font-bold text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
              >
                {mutation.isPending ? 'Matriculando…' : 'Matricular'}
              </button>
              <Link
                to="/inscripciones"
                className="inline-flex h-12 flex-1 items-center justify-center rounded-lg border-2 border-dash-primary text-sm font-bold text-dash-primary transition-all hover:bg-brand-50 active:scale-[0.98]"
              >
                Cancelar
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
